/**
 * B050011 仕入入力(物流業務)
 */

import { Column } from "@material-table/core";
import { Box } from "@mui/material";
import Big from "big.js";
import {
  B000111Biko,
  B000111Meisaibiko,
  B000190SokoNmrSokoshubetsuKbn,
  B040010Shain,
  B040010Shire,
  B040010Shohin,
  B050010Shire,
  B050010Shohin,
  B050010Tanka,
  B050010ZeiRitsu,
} from "clients/rest";
import {
  msgState,
  SCancelButton,
  SCodeSelect,
  SConfirmDialog,
  SConfirmDialogAction,
  SDateField,
  SEntryTab,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
} from "components";
import {
  B040010KanryoKbn,
  B050010KanryoKbn,
  ChohaKbn,
  getSCode,
  SCode,
  ShireUchiwakeKbn,
} from "messages/codes";
import { labels } from "messages/labels";
import {
  B000111,
  B000140,
  B000170,
  B000190,
  B000200,
  B000220,
  B000221,
} from "pages";
import { createRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, validDate } from "services";
import { digit } from "services/CalcService";
import {
  editKbn,
  meishoKbn,
  shireUchiwakeKbn,
  shohinShokuchiKbn,
  shokuchiKbn,
  tenyuryokuKbn,
} from "services/codes";
import { focusState, useFocus } from "states";

// export type B040010InputData = {
//   editKbn: string;
//   hachu: B040020GetResponse[];
// };

// 呼び出し時のプロパティ
type Props = Partial<{}>;

// フィールド定義
type Fields = Partial<{
  editKbn: string;
  hachudenpyoNo: string;
  shiredenpyoNo: string;
  juchudenpyoNo: string;
  shireYmd: string;
  shireCd: string;
  shireNm: string;
  shainCd: string;
  shainNm: string;
  bumonCd: string;
  bumonNm: string;
  bikoCd: string;
  biko: string;
  bikoCdBefore: string;
  chohaKbn: SCode;
  shireKinGokei: string;
  zeiKinGokei: string;
  gokei: string;
  getsujiYmd: string;
  zeiRitsu: string;
  shireShokuchiKbn: number;
  otodokeShokuchiKbn: number;
}>;

// 明細データ
type MeiData = Partial<{
  shiregyoNo: number;
  uchiwakeKbn: SCode;
  shohinCd: string;
  shohinShokuchiKbn: number;
  sokoCd: string;
  sokoNm: string;
  sokoshubetsuKbn: number;
  locationkanriKbn: number;
  locationCd: number;
  hachusu: string;
  shiresu: number;
  basetanka: string;
  shiretanka: string;
  shireKin: string;
  tenyuryokuKbn: number;
  shohinNmr: string;
  keijozumisu: string;
  meisaibikoCd: string;
  meisaibiko: string;
  meisaibikoCdBefore: string;
  kanryoKbn: SCode;
}>;

// 明細テーブルデータ定義
type Data = Partial<{
  shiregyoNo: number;
  juchudenpyoNo: string;
  juchugyoNo: string;
  uchiwakeKbn: string;
  shohinCd: string;
  shohinShokuchiKbn: number;
  // shohinshokuchiKbn: number; ?
  shohinNm: string;
  hachusu: string;
  keijozumisu: string;
  shiresu: number;
  shiretanka: number;
  basetanka: string;
  shireKin: string;
  tenyuryokuKbn: number;
  sokoCd: string;
  sokoNm: string;
  sokoshubetsuKbn: number;
  meisaibikoCd: string;
  meisaibiko: string;
  meisaibikoCdBefore: string;
  kazeiKbn: string;
  kanryoKbn: string;
}>;

// フィールド初期値
const defaultFields: Fields = {
  editKbn: editKbn.register,
  shiredenpyoNo: "",
  juchudenpyoNo: "",
  shireYmd: date(),
  shireCd: "",
  shireNm: "",
  shainCd: "",
  shainNm: "",
  bumonCd: "",
  bumonNm: "",
  bikoCd: "",
  biko: "",
  bikoCdBefore: "",
  chohaKbn: getSCode(ChohaKbn, "0"),
  shireKinGokei: "",
  zeiKinGokei: "",
  gokei: "",
  getsujiYmd: "",
  zeiRitsu: "",
  shireShokuchiKbn: undefined,
  otodokeShokuchiKbn: undefined,
};

// 明細フィールド初期値
const meiDefaultFields: ShireButuMeiFields = {
  meiTab: true,
  uchiwakeKbn: getSCode(ShireUchiwakeKbn, "0"),
  shohinCd: "",
  shohinShokuchiKbn: undefined,
  sokoCd: "",
  sokoNm: "",
  sokoshubetsuKbn: undefined,
  hachusu: "",
  shiresu: undefined,
  shiretanka: "",
  shireKin: "",
  tenyuryokuKbn: undefined,
  shohinNmr: "",
  keijozumisu: "",
  meisaibikoCd: "",
  meisaibiko: "",
  meisaibikoCdBefore: "",
  kanryoKbn: getSCode(B050010KanryoKbn, "0"),
};

// テーブル設定
const columns: Column<Data>[] = [
  { field: "shiregyoNo", title: "行　", width: "4rem", align: "right" },
  {
    field: "uchiwakeKbnView",
    title: "区分",
    width: "4rem",
    render: (data) => {
      return getSCode(ShireUchiwakeKbn, data.uchiwakeKbn ?? "0")?.label;
    },
  },
  { field: "shohinCd", title: "商品コード", width: "7rem" },
  { field: "shohinNm", title: "商品名", width: "15rem" },
  {
    field: "hachusu",
    title: "発注数　",
    width: "6rem",
    align: "right",
    render: (data) => digit(data.hachusu),
  },
  {
    field: "keijozumisu",
    title: "計上済数　",
    width: "6rem",
    align: "right",
    render: (data) => digit(data.keijozumisu),
  },
  {
    field: "shiresu",
    title: "仕入数　",
    width: "6rem",
    align: "right",
    render: (data) => digit(data.shiresu),
  },
  {
    field: "shiretanka",
    title: "仕入単価　",
    width: "6rem",
    align: "right",
    render: (data) => {
      let backgroundColor = "inherit";
      if (data.tenyuryokuKbn === tenyuryokuKbn.exists) {
        backgroundColor = "#FFFF00";
      }
      return <Box sx={{ backgroundColor }}>{digit(data.shiretanka)}</Box>;
    },
  },
  {
    field: "shireKin",
    title: "金額　",
    width: "6rem",
    align: "right",
    render: (data) => digit(data.shireKin),
  },
  { field: "sokoCd", title: "倉庫コード", width: "6rem" },
  { field: "sokoNm", title: "倉庫名", width: "10rem" },
  { field: "sokoNm", title: "ロケーション", width: "10rem" },
  { field: "meisaibiko", title: "仕入明細備考", width: "15rem" },
  { field: "meisaibikoCdBefore", hidden: true },
  {
    field: "kanryoKbnView",
    title: "完了",
    width: "4rem",
    render: (data) => {
      return getSCode(B040010KanryoKbn, data.kanryoKbn ?? "")?.label;
    },
  },
  { field: "kanryoKbn", title: "完了" },
  { field: "juchudenpyoNo", hidden: true },
  { field: "juchugyoNo", hidden: true },
  { field: "uchiwakeKbn", title: "内訳区分", hidden: true },
  { field: "shohinshokuchiKbn", title: "諸口区分", hidden: true },
  { field: "basetanka", hidden: true },
  { field: "tenyuryokuKbn", hidden: true },
  { field: "sokoshubetsuKbn", hidden: true },
  { field: "meisaibikoCdBefore", hidden: true },
  { field: "kazeiKbn", hidden: true },
];

//明細入力部
export type ShireButuMeiFields = MeiData &
  Partial<{
    meiTab: boolean;
  }>;

const focusId = "B050011";
const gamenId: string = "050011";

// B050011 仕入入力(物流業務)
export const B050011 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 初期設定
  // テーブル管理
  const tableRef = createRef();
  const [rows, setRows] = useState<Data[]>([]);
  const [rowCount, setRowCount] = useState(0);
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  const meiMethods = useForm<ShireButuMeiFields>({
    mode: "onBlur",
    defaultValues: meiDefaultFields,
  });

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // 遷移元データ取得用
  const navigate = useNavigate();
  const location = useLocation();
  //    const inputData = location.state as B040010InputData;
  // メッセージ表示
  const setMsg = useSetRecoilState(msgState);
  // 登録ボタンクリック時に出す確認ダイアログオープン用
  const [openCreateConfirm, setOpenCreateConfirm] = useState(false);
  // 更新ボタンクリック時に出す確認ダイアログオープン用
  const [openUpdateConfirm, setOpenUpdateConfirm] = useState(false);
  // // menusステート取得
  // const menus = useRecoilValue(menusState);
  // // 営業所権限取得
  // const eigyosyoKengen = menus["050"].pages[gamenId].eigyosyoKengen;

  // 活性設定
  // 発注.完了区分が「0:未完」かどうか
  // const isMikan = methods.watch("hachudenpyoNo") === "0";
  // 参照モードかどうか
  const isReference = methods.watch("editKbn") === editKbn.reference;
  // 修正モードかどうか
  const isUpdate = methods.watch("editKbn") === editKbn.update;
  // 仕入先.諸口が「1：諸口」かどうか
  const isShireShokuchi =
    methods.watch("shireShokuchiKbn") === shokuchiKbn.shokuchi;
  // 商品.諸口区分が「1:諸口」かどうか
  const isShohinShokuchiKbn =
    meiMethods.watch("shohinShokuchiKbn") === shohinShokuchiKbn.shokuchi;
  // 内訳区分が「2:値引」かどうか
  const isNebiki =
    meiMethods.watch("uchiwakeKbn")?.value === shireUchiwakeKbn.nebiki;
  // 内訳区分が「0:通常」かどうか
  const isTujo =
    meiMethods.watch("uchiwakeKbn")?.value === shireUchiwakeKbn.tujo;
  // 内訳区分が「3:見本」かどうか
  const isMihon =
    meiMethods.watch("uchiwakeKbn")?.value === shireUchiwakeKbn.mihon;
  // ベース単価、発注単価が一致するかどうか(手入力発注区分)
  const isTenyuryokuhachuKbn =
    Number(meiMethods.watch("basetanka")) ===
    Number(meiMethods.watch("shiretanka"));
  // ボタンの活性・非活性
  const [disabledDeleteDetail, setDisabledDeleteDetail] = useState(true);

  // 仕入日変更時
  const handleChangeShireYmd = (
    shireYmd: string = methods.getValues().shireYmd!
  ) => {
    if (shireYmd === "" || !validDate(shireYmd!)) return;

    (async () => {
      // 消費税取得
      await B050010ZeiRitsu(methods);

      // 明細入力部の単価再設定
      reacquisitionMeiTanka();

      // 明細が登録されている場合
      if (rows.length > 0) {
        // 明細再設定
        // 仕入単価が変更されていない商品コードを取得(重複排除)
        const shohinCdList: string[] = Array.from(
          new Set(
            rows
              // 手入力区分をチェック
              .filter((row) => row.tenyuryokuKbn === tenyuryokuKbn.none)
              // 内訳区分をチェック
              .filter((row) => row.uchiwakeKbn !== shireUchiwakeKbn.nebiki)
              .map((row) => row.shohinCd!)
          )
        );

        // 再設定する行が存在する場合
        if (shohinCdList.length > 0) {
          for (const shohinCd of shohinCdList) {
            B050010Tanka(methods, shohinCd, rows, navigate, setMsg, setRows);
          }
        }
        // 再設定する行が存在しない場合
        else {
          // 合計値(rowsに変更が無いため、明示的に処理を呼ぶ)
          handleGokeiCalculating();
        }
      }
    })();
  };

  // 明細入力部の単価再設定
  const reacquisitionMeiTanka = () => {
    // フォームのデータを取得する。
    const meiFields = meiMethods.getValues();
    // 明細入力部が設定されていない場合(商品コードで判断)
    if (meiFields.shohinCd === undefined) return;

    // 手入力区分が「1:あり」の場合
    if (meiFields.tenyuryokuKbn === tenyuryokuKbn.exists) return;

    // 内訳区分が「2:値引」の場合
    if (meiFields.uchiwakeKbn?.value === shireUchiwakeKbn.nebiki) return;

    // 商品関連項目表示処理を行う
    B050010Shohin(methods, meiMethods, navigate, setMsg);
  };

  // タブ切替
  const handleChangeMeiTab = () => {
    if (!meiMethods.watch("meiTab")) return;
    const fields = methods.getValues();
    meiMethods.reset();
    meiMethods.reset({
      ...meiMethods.getValues(),
      shohinCd: "",
    });
    meiMethods.setValue("uchiwakeKbn", getSCode(ShireUchiwakeKbn, "0"));
    meiMethods.setValue("meiTab", true);

    setDisabledDeleteDetail(true);
  };

  //  ダイアログ表示用（登録）
  const handleOpenCreateConfirm = () => {
    if (meiMethods.formState.isDirty) {
      setOpenCreateConfirm(true);
    } else {
      // handleClickCreateButton();
    }
  };

  //  ダイアログ表示用（更新）
  const handleOpenUpdateConfirm = () => {
    if (meiMethods.formState.isDirty) {
      setOpenUpdateConfirm(true);
    } else {
      // handleClickUpdateButton();
    }
  };

  // キャンセル押下時（登録）
  const handleCancelCreateConfirm = () => {
    setOpenCreateConfirm(false);
  };
  // キャンセル押下時（更新）
  const handleCancelUpdateConfirm = () => {
    setOpenUpdateConfirm(false);
  };

  // 登録
  const handleClickCreateButton = () => {};

  // 更新
  const handleClickUpdateButton = () => {};

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    // フォームのデータを取得する。
    const meiFields = meiMethods.getValues();

    // [明細データ.仕入数 > 0]の場合
    if (meiFields.shiresu! > 0) {
      // エラーメッセージをメッセージ欄に表示する。
      // setMsg(getClientError("B040010_006"));
      return;
    }

    // データを保存
    setRowData(data);
    if (typeof data.shiregyoNo !== "undefined") {
      // 明細入力部を編集に変更する
      meiMethods.setValue("meiTab", false);
      // 明細削除ボタンを活性化
      setDisabledDeleteDetail(false);
      // 明細入力部に値を設定する
      meiMethods.setValue("shiregyoNo", data.shiregyoNo);
      meiMethods.setValue(
        "uchiwakeKbn",
        getSCode(ShireUchiwakeKbn, data.uchiwakeKbn ?? "0")
      );
      meiMethods.setValue("shohinCd", data.shohinCd);
      meiMethods.setValue("shohinShokuchiKbn", data.shohinShokuchiKbn);
      meiMethods.setValue("sokoCd", data.sokoCd);
      meiMethods.setValue("sokoNm", data.sokoNm);
      meiMethods.setValue("sokoshubetsuKbn", data.sokoshubetsuKbn);
      meiMethods.setValue("hachusu", data.hachusu);
      meiMethods.setValue("shiresu", data.shiresu);
      meiMethods.setValue("tenyuryokuKbn", data.tenyuryokuKbn);
      meiMethods.setValue("basetanka", data.basetanka);
      // meiMethods.setValue("shiretanka", data.shiretanka);
      // meiMethods.setValue("shireKin", data.shireKin);
      // meiMethods.setValue("nyukayoteiYmd", data.nyukayoteiYmd);
      // meiMethods.setValue("meisaibikoCd", data.meisaibikoCd);
      // meiMethods.setValue("meisaibiko", data.meisaibiko);
      // meiMethods.setValue("meisaibikoCdBefore", data.meisaibikoCdBefore);
      // meiMethods.setValue("kanryoKbn", data.kanryoKbn);
      focus(12);
    }
  };

  // 内訳区分変更時
  const handleChangeUchiwakeKbn = () => {
    // ヘッダ部.希望納期を明細入力部に設定
    const fields = methods.getValues();
    const { uchiwakeKbn } = meiMethods.getValues();
    // 明細入力欄を全て初期化する。
    meiMethods.reset();
    meiMethods.reset({
      ...meiMethods.getValues(),
      shohinCd: "",
    });
    meiMethods.setValue(
      "uchiwakeKbn",
      getSCode(ShireUchiwakeKbn, uchiwakeKbn?.value!)
    );
  };

  // 仕入金計算
  const handleCalcShireKin = () => {
    const meiFields = meiMethods.getValues();
    const { uchiwakeKbn } = meiMethods.getValues();

    if (meiFields.shiretanka === "") {
      meiMethods.setValue("shireKin", "");
      return;
    }

    // 内訳区分が2:値引以外のとき
    if (uchiwakeKbn?.value !== shireUchiwakeKbn.nebiki) {
      // shiresu と shiretanka が null ではない場合
      if (meiFields.shiresu !== null && meiFields.shiretanka !== null) {
        // shireKin に設定する。shiresu × shiretanka
        const calcShireKin: Big = new Big(meiFields.shiresu!)
          .times(new Big(meiFields.shiretanka!))
          .round(0, Big.roundDown);
        meiMethods.setValue("shireKin", calcShireKin.toString());
      }
    }
  };

  //合計金額算出
  const handleGokeiCalculating = () => {
    // 明細が設定されていない場合
    if (rows.length === 0) {
      // 合計金額の初期化のみ行う
      methods.setValue("shireKinGokei", "");
      methods.setValue("zeiKinGokei", "");
      methods.setValue("gokei", "");
      return;
    }

    // フォームのデータを取得する。
    const fields = methods.getValues();

    // 仕入金額合計
    const shireKinGokei: Big = rows.reduce((acc: Big, row: Data): Big => {
      if (row.uchiwakeKbn === shireUchiwakeKbn.nebiki) {
        return acc.minus(new Big(row.shireKin ? row.shireKin : "0"));
      } else {
        return acc.plus(new Big(row.shireKin ? row.shireKin : "0"));
      }
    }, new Big("0"));

    // 消費税合計
    let zeiKinGokei: Big = new Big(0);
    zeiKinGokei = shireKinGokei
      .times(new Big(fields.zeiRitsu ? fields.zeiRitsu : "0"))
      .div(new Big("100"))
      .round(0, Big.roundDown);

    // 合計
    const gokei: Big = shireKinGokei.plus(zeiKinGokei);

    methods.setValue("shireKinGokei", shireKinGokei.toString());
    methods.setValue("zeiKinGokei", zeiKinGokei.toString());
    methods.setValue("gokei", gokei.toString());
  };

  // 仕入先変更
  const handleChangeShire = () => {
    // フォームのデータを取得する。
    const fields = methods.getValues();
    if (fields.shireCd === "") {
      methods.setValue("shireNm", "");
      methods.setValue("shainCd", "");
      methods.setValue("shainNm", "");
      methods.setValue("bumonCd", "");
      methods.setValue("bumonNm", "");
      methods.setValue("shireShokuchiKbn", undefined);
      return;
    }

    // 変更されていない場合は切替しない。
    if (!methods.getFieldState("shireCd").isDirty) {
      return;
    }
    // 仕入先関連項目設定
    B050010Shire(methods, navigate, setMsg);
    methods.unregister("shireCd", { keepValue: true });
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="仕入入力"
          gridTemplateColumns="6rem 10rem 1rem 5rem 10rem 5rem 1rem 6rem 10rem 10rem 10rem"
        >
          <SLabel>発注No.</SLabel>
          <SNumberField
            name="hachudenpyoNoF"
            {...register(1)}
            size={10}
            code
            // dialog={
            //   <B000160 eigyosyoKengen={eigyosyoKengen} gamenId={gamenId} />
            // }
            // handleSubmit={(data: any) => {
            //   methods.setValue("hachudenpyoNoF", data.hachudenpyoNo);
            // }}
            // disabled={isMikan || isReference || isUpdate}
          />

          <SLabel required gridColumn="1">
            仕入日
          </SLabel>
          <SDateField
            label="仕入日"
            name="hachuYmd"
            required
            {...register(2)}
            disabled={isReference}
            onBlur={() => handleChangeShireYmd()}
            onAccept={(date: string) => {
              handleChangeShireYmd(date);
            }}
          />

          <SLabel required gridColumn="1">
            仕入先
          </SLabel>
          <SNumberField
            label="仕入先コード"
            name="shireCd"
            required
            size={6}
            {...register(3)}
            onBlur={() => handleChangeShire()}
            dialog={<B000170 />}
            handleSubmit={(data: any) => {
              methods.setValue("shireCd", data.shireCd);
              // 仕入先表示
              B040010Shire(methods, navigate, setMsg);
            }}
            disabled={isReference}
            code
          />
          <STextField
            name="shireNm"
            size={40}
            {...register(4)}
            gridColumn="span 4"
            disabled={!isShireShokuchi || isReference}
          />

          <SLabel gridColumn="1" required>
            仕入担当者
          </SLabel>
          <SNumberField
            label="仕入担当者コード"
            name="shainCd"
            required
            size={7}
            {...register(5)}
            onBlur={() => B040010Shain(methods, navigate, setMsg)}
            dialog={<B000140 />}
            handleSubmit={(data: any) => {
              methods.setValue("shainCd", data.shainCd);
              methods.setValue("shainNm", data.shainNm);

              B040010Shain(methods, navigate, setMsg);
            }}
            disabled={isReference}
            code
          />
          <STextField name="shainNm" disabled gridColumn="span 4" size={40} />

          <SLabel gridColumn="8">仕入担当部門</SLabel>
          <STextField name="bumonNm" disabled size={20} />

          <SLabel gridColumn="1">仕入備考</SLabel>
          <SNumberField
            name="bikoCd"
            onBlur={() => B000111Biko(methods, navigate, setMsg)}
            dialog={<B000111 meishoKbn={meishoKbn.biko} />}
            handleSubmit={(data: any) => {
              methods.setValue("bikoCd", data?.meishoCd);
              methods.setValue("biko", data?.meishoNm1);
              methods.setValue("bikoCdBefore", data?.meishoCd);
            }}
            size={3}
            {...register(6)}
            disabled={isReference}
            code="true"
          />
          <STextField
            name="biko"
            gridColumn="span 4"
            size={40}
            {...register(7)}
            disabled={isReference}
          />

          <SLabel required gridColumn="8">
            仕入帳端
          </SLabel>
          <SCodeSelect
            label="仕入帳端"
            name="chohaKbn"
            required
            options={ChohaKbn}
            {...register(8)}
            //    onChange={() => handleChangeUchiwakeKbn()}
            disabled={isReference}
          />

          <SLabel gridColumn="9" gridRow="7">
            仕入金額合計
          </SLabel>
          <SLabel gridColumn="10" gridRow="7">
            消費税
          </SLabel>
          <SLabel gridColumn="11" gridRow="7">
            合計
          </SLabel>
          <SNumberField
            name="hachuKinGokei"
            disabled
            align="right"
            gridColumn="9"
            gridRow="8"
            size={11}
          />
          <SNumberField
            name="zeiKinGokei"
            disabled
            align="right"
            gridColumn="10"
            gridRow="8"
            size={10}
          />
          <SNumberField
            name="gokei"
            disabled
            align="right"
            gridColumn="11"
            gridRow="8"
            size={11}
          />
        </SFrame>
      </FormProvider>
      <SFrame>
        <STable
          keyName="hachugyoNo"
          data={rows}
          columns={columns}
          totalCount={rowCount}
          tableRef={tableRef}
          width={1260}
          height={320}
          //    handleDoubleClick={
          //      isReference ? undefined : handleDoubleClickTableRow
          //    }
          rowSelectable={meiMethods.watch("meiTab")}
        />
      </SFrame>

      <FormProvider {...meiMethods}>
        <SFrame>
          <SEntryTab
            name="meiTab"
            handleOk={handleChangeMeiTab}
            disabled={isReference}
          ></SEntryTab>
          <SFrame gridTemplateColumns="8rem 10rem 5rem 9rem 8rem 8rem 8rem 8rem 10.5rem">
            <SLabel required>区分</SLabel>
            <SLabel required>商品コード</SLabel>
            <SLabel required gridColumn="span 2">
              倉庫
            </SLabel>
            <SLabel>ロケーション</SLabel>
            <SLabel>発注数</SLabel>

            {isTujo || isMihon ? (
              <>
                <SLabel required>仕入数</SLabel>
              </>
            ) : (
              <>
                <SLabel>仕入数</SLabel>
              </>
            )}

            {isTujo || isMihon ? (
              <>
                <SLabel required>仕入単価</SLabel>
              </>
            ) : (
              <>
                <SLabel>仕入単価</SLabel>
              </>
            )}

            <SLabel required>仕入金額</SLabel>

            <SCodeSelect
              label="区分"
              name="uchiwakeKbn"
              required
              options={ShireUchiwakeKbn}
              {...register(9)}
              gridColumn="1"
              onChange={() => handleChangeUchiwakeKbn()}
              disabled={isReference}
            />
            <SNumberField
              label="商品コード"
              name="shohinCd"
              size={10}
              required
              {...register(10)}
              dialog={<B000200 />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("shohinCd", data.shohinCd);
                meiMethods.setValue("shohinShokuchiKbn", data.shokuchiKbn);
                // 商品関連項目表示
                B050010Shohin(methods, meiMethods, navigate, setMsg);
              }}
              // onBlur={() =>
              //   // 商品コード変更
              //   B050010HandleChangeShohinCd(
              //     methods,
              //     meiMethods,
              //     navigate,
              //     setMsg
              //   )
              // }
              disabled={isReference}
              code
            />
            <STextField
              label="倉庫コード"
              name="sokoCd"
              {...register(11)}
              size={3}
              required
              onBlur={() =>
                B000190SokoNmrSokoshubetsuKbn(meiMethods, navigate, setMsg)
              }
              dialog={<B000190 />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("sokoCd", data?.sokoCd);
                meiMethods.setValue("sokoNm", data?.sokoNmr);
                meiMethods.setValue("sokoshubetsuKbn", data?.sokoshubetsuKbn);
              }}
              disabled={isReference}
              code
            />
            <STextField name="sokoNm" disabled />

            <STextField
              name="locationCd"
              label="ロケーションコード"
              size={14}
              location
              required
              dialog={<B000221 />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("locationCd", data.locationCd);
              }}
              {...register(12)}
            />

            <SNumberField
              label="発注数"
              name="hachusu"
              size={6}
              align="right"
              disabled={isNebiki || isReference}
              onBlur={() => {
                handleCalcShireKin();
              }}
              dialog={<B000220 shohinCd={meiMethods.watch("shohinCd")} />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("shohinCd", data.shohinCd);
                meiMethods.setValue("sokoCd", data.sokoCd);
                meiMethods.setValue("sokoNm", data.sokoNm);
                meiMethods.setValue("sokoshubetsuKbn", data.sokoshubetsuKbn);

                // 商品関連項目表示
                B050010Shohin(methods, meiMethods, navigate, setMsg);
              }}
            />

            {isTujo || isMihon ? (
              <>
                <SNumberField
                  label="仕入数"
                  name="shiresu"
                  size={6}
                  align="right"
                  {...register(13)}
                  disabled={isNebiki || isReference}
                  onBlur={() => {
                    handleCalcShireKin();
                  }}
                  dialog={<B000220 shohinCd={meiMethods.watch("shohinCd")} />}
                  handleSubmit={(data: any) => {
                    meiMethods.setValue("shohinCd", data.shohinCd);
                    meiMethods.setValue("sokoCd", data.sokoCd);
                    meiMethods.setValue("sokoNm", data.sokoNm);
                    meiMethods.setValue(
                      "sokoshubetsuKbn",
                      data.sokoshubetsuKbn
                    );

                    // 商品関連項目表示
                    B040010Shohin(methods, meiMethods, navigate, setMsg);
                  }}
                  required
                />
              </>
            ) : (
              <>
                <SNumberField
                  label="仕入数"
                  name="shiresu"
                  size={6}
                  align="right"
                  {...register(13)}
                  disabled={isNebiki || isReference}
                  onBlur={() => {
                    handleCalcShireKin();
                  }}
                  dialog={<B000220 shohinCd={meiMethods.watch("shohinCd")} />}
                  handleSubmit={(data: any) => {
                    meiMethods.setValue("shohinCd", data.shohinCd);
                    meiMethods.setValue("sokoCd", data.sokoCd);
                    meiMethods.setValue("sokoNm", data.sokoNm);
                    meiMethods.setValue(
                      "sokoshubetsuKbn",
                      data.sokoshubetsuKbn
                    );

                    // 商品関連項目表示
                    B040010Shohin(methods, meiMethods, navigate, setMsg);
                  }}
                />
              </>
            )}

            {isTujo || isMihon ? (
              <>
                <SNumberField
                  label="仕入単価"
                  name="shiretanka"
                  size={9}
                  align="right"
                  {...register(14)}
                  disabled={isNebiki || isReference}
                  required
                  onBlur={() => {
                    handleCalcShireKin();
                  }}
                />
              </>
            ) : (
              <>
                <SNumberField
                  label="仕入単価"
                  name="shiretanka"
                  size={9}
                  align="right"
                  {...register(14)}
                  disabled={isNebiki || isReference}
                  onBlur={() => {
                    handleCalcShireKin();
                  }}
                />
              </>
            )}

            <SNumberField
              label="仕入金額"
              name="shireKin"
              required
              size={9}
              align="right"
              {...register(15)}
              disabled={!isNebiki || isReference}
            />

            <SLabel gridColumn="1/5">商品名</SLabel>
            <SLabel gridColumn="5/6">計上済数</SLabel>
            <SLabel gridColumn="6/10">仕入明細備考</SLabel>
            <STextField
              name="shohinNm"
              gridColumn="span 4"
              {...register(16)}
              disabled={!isShohinShokuchiKbn || isReference}
            />
            <SNumberField
              name="shohinNm"
              gridColumn="span 1"
              disabled={!isShohinShokuchiKbn || isReference}
            />

            <SNumberField
              name="meisaibikoCd"
              {...register(17)}
              size={3}
              onBlur={() => B000111Meisaibiko(meiMethods, navigate, setMsg)}
              dialog={<B000111 meishoKbn="7" />}
              handleSubmit={(data: any) => {
                meiMethods.setValue("meisaibikoCd", data?.meishoCd);
                meiMethods.setValue("meisaibiko", data?.meishoNm1);
                meiMethods.setValue("meisaibikoCdBefore", data?.meishoCd);
              }}
              gridColumn="6/6"
              disabled={isReference}
              code="true"
            />
            <STextField
              name="meisaibiko"
              gridColumn="7/10"
              {...register(18)}
              disabled={isReference}
            />

            <SCancelButton
              label={labels.buttons.deleteDetail}
              {...register(20)}
              gridRow="7"
              gridColumn="1"
              //  onClick={handleClickDeleteMeiButton}
              disabled={disabledDeleteDetail}
            />
            {meiMethods.watch("meiTab") ? (
              <SNormalButton
                label={labels.buttons.createDetail}
                {...register(19)}
                gridRow="7"
                gridColumn="9"
                align="right"
                //  onClick={handleClickCreateMeiButton}
                disabled={isReference}
              />
            ) : (
              <SNormalButton
                label={labels.buttons.updateDetail}
                {...register(19)}
                gridRow="7"
                gridColumn="9"
                align="right"
                //  onClick={handleClickUpdateMeiButton}
              />
            )}
          </SFrame>
          <SFrame gridTemplateColumns="7rem 68rem 4rem">
            <SCancelButton
              label={labels.buttons.delete}
              gridColumn={1}
              {...register(22)}
              disabled={!isUpdate}
              dialog={
                <SConfirmDialog
                  title="仕入データ削除"
                  msg="仕入データを削除します。よろしいですか？"
                />
              }
              dialogAction="confirm"
              //  handleSubmit={handleClickDeleteButton}
            />
            {!isReference && (
              <>
                {methods.getValues().hachudenpyoNo === "" ? (
                  <SNormalButton
                    label={labels.buttons.create}
                    align="right"
                    {...register(21)}
                    //  onClick={() => handleOpenCreateConfirm()}
                  />
                ) : (
                  <SNormalButton
                    label={labels.buttons.update}
                    align="right"
                    {...register(21)}
                    //  onClick={() => handleOpenUpdateConfirm()}
                  />
                )}
              </>
            )}
          </SFrame>
        </SFrame>
        <SConfirmDialogAction
          msg={labels.dialog.createCheck}
          onOk={handleClickCreateButton}
          onCancel={handleCancelCreateConfirm}
          isOpen={openCreateConfirm}
        />
        <SConfirmDialogAction
          msg={labels.dialog.updateCheck}
          onOk={handleClickUpdateButton}
          onCancel={handleCancelUpdateConfirm}
          isOpen={openUpdateConfirm}
        />
      </FormProvider>
    </>
  );
};
